"use client";

import type { ControllersCreateDisposableAliasBody } from "@gosaas/types";
import { IconCheck, IconCopy, IconLoader2 } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import * as React from "react";
import { Button, buttonVariants } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
	type CreateAliasResponseData,
	DISPOSABLE_TTL_OPTIONS,
} from "@/data/custom/disposable";
import { cn } from "@/lib/utils";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";

const domainSelectTriggerClass =
	"h-11 w-full border-0 bg-background/90 shadow-none ring-1 ring-border/50 transition-[background-color,box-shadow] hover:bg-background hover:ring-border/65";

export function DisposableHomeHero() {
	const [ttl, setTtl] = React.useState("30m");
	const [mailHost, setMailHost] = React.useState("");

	const domainsQ = useQuery({
		queryKey: ["disposable-domains"],
		queryFn: async () => {
			const res = await requestWithHandling(() =>
				apiClient.api.disposableDomainsList(),
			);
			if (res.code !== "SUCCESS" || res.data === undefined) {
				throw new TypedClientApiError(
					res.message || res.code || "Request failed",
					undefined,
					null,
					res.code,
				);
			}
			const domains = (res.data as { domains?: string[] }).domains;
			if (!domains?.length) {
				throw new TypedClientApiError(
					"No domains configured",
					undefined,
					null,
					"CONFIG",
				);
			}
			return domains;
		},
		staleTime: 300_000,
		retry: 2,
	});

	React.useEffect(() => {
		const list = domainsQ.data;
		if (!list?.length) return;
		setMailHost((prev) => (prev && list.includes(prev) ? prev : list[0]));
	}, [domainsQ.data]);

	const canCreate = domainsQ.isSuccess && mailHost !== "";

	const { data, isPending, isError, error, refetch, isFetching } = useQuery<
		CreateAliasResponseData,
		Error
	>({
		queryKey: ["marketing-disposable-alias", ttl, mailHost],
		queryFn: async () => {
			const body: ControllersCreateDisposableAliasBody = {
				ttl,
				mail_domain: mailHost,
			};
			const res = await requestWithHandling(() =>
				apiClient.api.disposableAliasesCreate(body),
			);
			if (res.code !== "SUCCESS" || res.data === undefined) {
				throw new TypedClientApiError(
					res.message || res.code || "Request failed",
					undefined,
					null,
					res.code,
				);
			}
			return res.data as unknown as CreateAliasResponseData;
		},
		enabled: canCreate,
	});

	const [copied, setCopied] = React.useState(false);

	const copyAddress = React.useCallback(() => {
		if (!data?.address) return;
		void navigator.clipboard.writeText(data.address).then(() => {
			setCopied(true);
			window.setTimeout(() => setCopied(false), 2000);
		});
	}, [data?.address]);

	return (
		<div className="mx-auto w-full max-w-3xl">
			<div
				className={cn(
					"rounded-3xl bg-muted/20 p-6 ring-1 ring-border/30 sm:p-8 lg:p-10",
					"dark:bg-muted/10",
				)}
			>
				<div className="grid gap-10 lg:grid-cols-[minmax(0,15.5rem)_minmax(0,1fr)] lg:items-start lg:gap-12">
					<div className="flex flex-col gap-8">
						<div className="space-y-3">
							<p className="text-sm font-medium text-foreground">Lifetime</p>
							<ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-2">
								{DISPOSABLE_TTL_OPTIONS.map((o) => {
									const active = ttl === o.value;
									return (
										<li key={o.value} className="min-w-0">
											<button
												type="button"
												aria-pressed={active}
												onClick={() => setTtl(o.value)}
												className={cn(
													"w-full rounded-xl px-3 py-2.5 text-left text-sm font-medium transition-colors",
													active
														? "bg-foreground text-background ring-1 ring-foreground/20"
														: "bg-background/60 text-foreground ring-1 ring-border/45 hover:bg-background hover:ring-border/60",
												)}
											>
												<span className="block truncate">{o.label}</span>
											</button>
										</li>
									);
								})}
							</ul>
						</div>

						<div className="space-y-2">
							<Label
								htmlFor="home-domain"
								className="text-sm font-medium text-foreground"
							>
								Domain
							</Label>
							{domainsQ.isError ? (
								<p className="text-sm text-destructive">
									{domainsQ.error instanceof Error &&
									domainsQ.error.message &&
									domainsQ.error.message !== "An unknown error occurred."
										? domainsQ.error.message
										: "Could not load domains."}{" "}
									<button
										type="button"
										className="font-medium underline underline-offset-2"
										onClick={() => void domainsQ.refetch()}
									>
										Retry
									</button>
								</p>
							) : (
								<Select
									value={mailHost || undefined}
									onValueChange={(v) => {
										if (v) setMailHost(v);
									}}
									disabled={!domainsQ.data?.length}
								>
									<SelectTrigger
										id="home-domain"
										className={cn(domainSelectTriggerClass, "rounded-xl")}
									>
										<SelectValue
											placeholder={
												domainsQ.isPending ? "Loading…" : "Choose domain"
											}
										/>
									</SelectTrigger>
									<SelectContent>
										{(domainsQ.data ?? []).map((d) => (
											<SelectItem
												key={d}
												value={d}
												className="font-mono text-sm"
											>
												{d}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							)}
						</div>
					</div>

					<div className="relative flex min-h-[12rem] flex-col gap-5 lg:min-h-[13.5rem]">
						<div className="space-y-2">
							<p className="text-sm font-medium text-foreground">
								Your address
							</p>
							{!canCreate ? (
								domainsQ.isPending ? (
									<div className="space-y-3" aria-busy="true">
										<Skeleton className="h-14 w-full rounded-2xl sm:h-16" />
										<Skeleton className="h-4 w-2/3 max-w-xs rounded-md" />
									</div>
								) : domainsQ.isError ? (
									<p className="rounded-2xl bg-background/50 px-4 py-5 text-sm leading-6 text-muted-foreground ring-1 ring-border/40 sm:px-5">
										Resolve the domain error on the left, then we will create
										your address here.
									</p>
								) : (
									<p className="rounded-2xl bg-background/50 px-4 py-5 text-sm leading-6 text-muted-foreground ring-1 ring-border/40 sm:px-5">
										Pick a domain on the left to generate an address.
									</p>
								)
							) : isPending ? (
								<div className="space-y-3" aria-busy="true">
									<Skeleton className="h-14 w-full rounded-2xl sm:h-16" />
									<Skeleton className="h-4 w-2/3 max-w-xs rounded-md" />
								</div>
							) : isError ? (
								<div className="space-y-4 rounded-2xl bg-background/70 px-4 py-5 ring-1 ring-border/50 sm:px-5 sm:py-6">
									<p className="text-sm text-destructive">
										{error instanceof Error
											? error.message
											: "Could not create an address."}
									</p>
									<Button
										type="button"
										size="sm"
										variant="outline"
										className="border-0 ring-1 ring-border/50"
										onClick={() => void refetch()}
									>
										Try again
									</Button>
								</div>
							) : (
								<>
									<div className="relative rounded-2xl bg-background/85 px-4 py-5 ring-1 ring-border/50 sm:px-6 sm:py-6">
										<p className="font-mono text-base leading-relaxed break-all text-foreground tracking-tight sm:text-lg sm:leading-8">
											{data.address}
										</p>
										{isFetching && !isPending ? (
											<div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-background/60 backdrop-blur-[1px]">
												<IconLoader2
													className="size-6 animate-spin text-muted-foreground"
													aria-hidden
												/>
											</div>
										) : null}
									</div>
									<p className="text-sm leading-6 text-muted-foreground">
										Expires{" "}
										<time dateTime={data.expires_at}>
											{new Date(data.expires_at).toLocaleString(undefined, {
												dateStyle: "medium",
												timeStyle: "short",
											})}
										</time>
									</p>
									<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
										<Button
											type="button"
											variant="outline"
											className="h-11 gap-2 border-0 bg-background/70 shadow-none ring-1 ring-border/50 hover:bg-background sm:shrink-0"
											disabled={!data.address}
											onClick={() => void copyAddress()}
										>
											{copied ? (
												<IconCheck className="size-4" aria-hidden />
											) : (
												<IconCopy className="size-4" aria-hidden />
											)}
											{copied ? "Copied" : "Copy"}
										</Button>
										<Link
											to="/inbox/$localPart"
											params={{ localPart: data.local_part }}
											search={{ page: 1 }}
											className={cn(
												buttonVariants({ variant: "default" }),
												"h-11 flex-1 rounded-xl text-center sm:min-w-[11rem]",
											)}
										>
											Open inbox
										</Link>
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			</div>

			<p className="mx-auto mt-8 max-w-lg text-center text-sm leading-6 text-pretty text-muted-foreground">
				Save the inbox link. Only that URL opens your mail—I cannot recover lost
				links, by design.
			</p>
		</div>
	);
}
